import { useState, useEffect, useRef, Fragment } from 'react';
import classNames from 'classnames';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'components/shared/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import { useGlobalModules } from 'contexts';
import Rating from 'components/shared/reviewsIO/rating';
import ProductLabels from 'components/shared/productLabels/productLabels';
import styles from './productGallery.module.scss';
import VideoPlayer from '../../../shared/video/video';
import Play from '../../../shared/play';
import WishlistButton from '../../../shared/wishlist-button';

const ProductGallery = ({ thumbnail, productMedia, productData, freeItemLabel }) => {
  const [activeImage, setActiveImage] = useState(0);
  const activeImageRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    slidesPerView: 1,
    slideChanged(s) {
      setCurrentSlide(s.track?.details?.abs);
    },
  });
  const isMobile = useIsMobile();
  const globalModules = useGlobalModules();
  const { translations } = globalModules || {};
  const { hasWebshop } = globalModules.settings.locale;

  useEffect(() => {
    // when switching from mobile to desktop and back
    setCurrentSlide(0);
  }, [isMobile]);

  // This effect will only be called on mobile devices
  useEffect(() => {
    activeImageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeImage]);

  return (
    <div className={styles.productGallery}>
      <div className={styles.productPictures}>
        {isMobile ? (
          // Mobile images in a Slider
          <>
            <div className={classNames('keen-slider', styles.slider)} ref={sliderRef}>
              {productMedia?.map((media, index) => {
                if (media.type === 'Image') {
                  return (
                    <Fragment key={`mobile-${media.uid}`}>
                      <Image
                        ref={activeImage === index ? activeImageRef : null}
                        className={classNames(styles.productImageSlider, 'keen-slider__slide')}
                        src={media.image}
                        loading="eager"
                        layout="fill"
                        priority
                      />
                    </Fragment>
                  );
                }
                if (media.type === 'Video' && media.video) {
                  return (
                    <VideoPlayer
                      key={`mobile-${media.video.title}`}
                      className={classNames('keen-slider__slide', styles.productVideoSlider)}
                      thumbnail={media.videoThumbnail}
                      source={media.video}
                      aspectRatio={media.videoAspectRatio || 16 / 9}
                    />
                  );
                }
                return null;
              })}
            </div>
            <Rating
              SKU={productData?.shopifyData?.productSKU}
              locale={productData?.locale}
              className={styles.mobileRating}
            />
            {hasWebshop && isMobile && (
              <WishlistButton
                className={styles.wishlistIcon}
                productData={productData}
                shopifyData={productData.shopifyData}
              />
            )}
            <ProductLabels freeItemLabel={freeItemLabel} customLabel={productData.customLabel} />
          </>
        ) : (
          // Desktop images
          <>
            {productMedia?.map((media, index) => {
              if (media.type === 'Image') {
                return (
                  <Image
                    className={classNames(styles.activeProductImage, {
                      [styles.displayMedia]: index === activeImage,
                    })}
                    src={media.image || thumbnail}
                    loading="eager"
                    layout="fill"
                    sizes="50vw"
                    priority
                  />
                );
              }
              if (media.type === 'Video' && media.video) {
                return (
                  <VideoPlayer
                    key={`mobile-${media.video.title}`}
                    className={classNames(styles.activeProductVideo, {
                      [styles.displayMedia]: index === activeImage,
                    })}
                    thumbnail={media.videoThumbnail}
                    source={media.video}
                    aspectRatio={media.videoAspectRatio || 16 / 9}
                  />
                );
              }
              return null;
            })}
            <ProductLabels freeItemLabel={freeItemLabel} customLabel={productData.customLabel} />
          </>
        )}
      </div>

      <div className={styles.productGalleryRow}>
        {!isMobile && (
          <>
            {productMedia?.map((media, index) => (
              <button
                key={`desktop-${media.uid}`}
                type="button"
                tabIndex={0}
                className={classNames(styles.productGalleryPicture, {
                  [styles.activeGalleryPicture]: activeImage === index,
                })}
                onClick={() => setActiveImage(index)}
                aria-label={`${translations?.accessibility?.goToSlide || 'Go to slide'} ${
                  index + 1
                }`}
              >
                <Image
                  src={media.thumbnail || media.image}
                  loading="eager"
                  layout="fill"
                  sizes="10vw"
                  priority
                />
                {media.type === 'Video' && media.video && <Play width="2rem" height="2rem" />}
              </button>
            ))}
          </>
        )}
        {isMobile && (
          <div>
            {[...Array(slider?.current?.slides?.length || productMedia?.length)?.keys()]?.map(
              idx => (
                <button
                  key={idx}
                  type="button"
                  tabIndex={0}
                  aria-label={`${translations?.accessibility?.goToSlide || 'Go to slide'} ${
                    idx + 1
                  }`}
                  className={classNames(
                    styles.productGallerySphere,
                    {
                      selectedSphere: currentSlide === idx,
                    },
                    currentSlide === idx ? styles.selectedSphere : ''
                  )}
                  onClick={() => slider.current.moveToIdx(idx)}
                />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductGallery;
